/**
 * Interface for assessment scores
 */
interface AssessmentScores {
  armScore?: number | null;
  armScorePR?: number | null;
  wcpmScore?: number | null;
  equatedWCPM?: number | null;
  equatedWCPMPR?: number | null;
  lexile?: number | null;
  dra?: number | null;
  esriScore?: number | null;
  esriScore_PR?: number | null;
  phonAwareScore?: number | null;
  phonAwareScore_PR?: number | null;
  vocabSize?: number | null;
  bkScore?: number | null;
  bkScore_PR?: number | null;
  compScore?: number | null;
  compScore_PR?: number | null;
  decodeScore?: number | null;
  decodeScore_PR?: number | null;
  ISIP_overall_universal?: number | null;
ISIP_overall_universal_PR?: number | null;
  vocabularyScreenerScore?: number | null;
  [key: string]: any;
}

/**
 * Interface for assessment object
 */
interface Assessment {
  scores?: AssessmentScores;
  [key: string]: any;
}

/**
 * Result interface for extracted score
 */
interface ExtractedScore {
  score: number | null;
  PR: number | null;
}

/**
 * Extracts score and percentile rank from an assessment object based on metric and scale IDs
 * @param assessment - The assessment object containing scores
 * @param metricId - The metric ID (e.g., 'ARM', 'ORF', 'SightRecognition', etc.)
 * @param scaleId - The scale ID (e.g., 'WCPM', 'Lexile', 'DRA', etc.)
 * @returns Object containing score and PR (percentile rank)
 */
export const extractScoreFromAssessment = (
  assessment: Assessment | null | undefined, 
  metricId: string, 
  scaleId: string
): ExtractedScore => {
  if (!assessment?.scores) return { score: null, PR: null };
  
  let score: number | null = null;
  let PR: number | null = null;
  
  // Extract score based on metricId
  if (metricId === 'ARM') {
    score = assessment.scores.armScore ?? null;
    PR = assessment.scores.armScorePR ?? null;
  } else if (metricId === 'ORF') {
    if (scaleId === 'WCPM' || scaleId === 'equatedWCPM') {
      score = assessment.scores.wcpmScore ?? assessment.scores.equatedWCPM ?? null;
      PR = assessment.scores.equatedWCPMPR ?? null;
    } else if (scaleId === 'Lexile') {
      score = assessment.scores.lexile ?? null;
    } else if (scaleId === 'DRA') {
      score = assessment.scores.dra ?? null;
    }
  } else if (metricId === 'SightRecognition') {
    score = assessment.scores.esriScore ?? null;
    PR = assessment.scores.esriScore_PR ?? null;
  } else if (metricId === 'PhonologicalAwareness') {
    score = assessment.scores.phonAwareScore ?? null;
    PR = assessment.scores.phonAwareScore_PR ?? null;
  } else if (metricId === 'VocabSize') {
    score = assessment.scores.vocabSize ?? null;
  } else if (metricId === 'bkScore') {
    score = assessment.scores.bkScore ?? null;
    PR = assessment.scores.bkScore_PR ?? null;
  } else if (metricId === 'compScore') {
    score = assessment.scores.compScore ?? null;
    PR = assessment.scores.compScore_PR ?? null;
  } else if (metricId === 'decodeScore') {
    score = assessment.scores.decodeScore ?? null;
    PR = assessment.scores.decodeScore_PR ?? null;
  } else if (metricId === 'ISIP_overall_universal') {
score = assessment.scores.ISIP_overall_universal ?? null;
PR = assessment.scores.ISIP_overall_universal_PR ?? null;
  } else if (metricId === 'vocabularyScreenerScore') {
    score = assessment.scores.vocabularyScreenerScore ?? null;
    PR = assessment.scores.vocabularyScreenerScore_PR ?? null;
  } else if (metricId === 'Lexile') {
    score = assessment.scores.lexile ?? null;
    // Note: Lexile doesn't typically have a PR field, but check if one exists
    PR = assessment.scores.lexile_PR ?? null;
  }
  
  return { score, PR };
}; 